import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { cvAPI } from '../services/api';
import { fmtDate, initials } from '../utils/helpers';
import { extractError } from '../context/AuthContext';
import {
  Upload, FileText, Download, Send, Save, RefreshCw,
  Plus, Trash2, ChevronDown, ChevronUp, GripVertical,
  User, Briefcase, Globe, Award, GraduationCap, X, Check, Eye, Languages,
} from 'lucide-react';
import toast from 'react-hot-toast';
import './CVConversion.css';

const STEPS = ['upload', 'extract', 'edit', 'done'];

export default function CVConversion() {
  const [step,      setStep]      = useState('upload');
  const [file,      setFile]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [cvData,    setCvData]    = useState(null);
  const [cvId,      setCvId]      = useState(null);
  const [cvList,    setCvList]    = useState([]);
  const [sendModal,      setSendModal]      = useState(false);
  const [sendEmail,      setSendEmail]      = useState('');
  const [emailSurCv,     setEmailSurCv]     = useState('');
  const [expanded,       setExpanded]       = useState({ personal: true, experiences: true });
  const [selectedCv,     setSelectedCv]     = useState(null);
  // Translation state
  const [isTranslated,   setIsTranslated]   = useState(false);
  const [translatedUrl,  setTranslatedUrl]  = useState(null);
  const [translating,    setTranslating]    = useState(false);
  const [frenchCvData,   setFrenchCvData]   = useState(null);

  useEffect(() => { fetchCVList(); }, []);

  const fetchCVList = async () => {
    try {
      const res = await cvAPI.list();
      setCvList(res.data || []);
    } catch { setCvList([]); }
  };

  // ── Ouvrir un CV existant depuis la liste pour le rééditer ──
  const _resetTranslation = () => {
    setIsTranslated(false);
    setTranslatedUrl(null);
    setFrenchCvData(null);
  };

  const openCvForEdit = (cv) => {
    setSelectedCv(cv);
    setCvId(cv.id);
    setEmailSurCv(cv.email_sur_cv || '');
    setStep('edit');
    setFile(null);
    // If an English version exists, restore both so the toggle works immediately
    if (cv.json_cv_en) {
      setCvData(cv.json_cv_en);
      setFrenchCvData(cv.json_cv);
      setIsTranslated(true);
      setTranslatedUrl(null);
    } else {
      setCvData(cv.json_cv);
      _resetTranslation();
    }
  };

  const closeCvEdit = () => {
    setSelectedCv(null);
    setCvId(null);
    setCvData(null);
    setEmailSurCv('');
    setStep('upload');
    setFile(null);
    _resetTranslation();
  };

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) { setFile(accepted[0]); setSelectedCv(null); }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'text/plain': [],
    },
    maxFiles: 1,
  });

  const handleConvert = async () => {
    if (!file) return;
    setLoading(true);
    setStep('extract');
    try {
      const res = await cvAPI.convert(file);
      setCvData(res.data.json_cv);
      setEmailSurCv(res.data.email_sur_cv || '');
      setCvId(null);
      setSelectedCv(null);
      _resetTranslation();
      setStep('edit');
    } catch (e) {
      toast.error(extractError(e));
      setStep('upload');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (cvId) {
        await cvAPI.update(cvId, { json_cv: cvData });
        toast.success('CV mis à jour');
      } else {
        const res = await cvAPI.save({ json_cv: cvData });
        setCvId(res.data.id);
        toast.success('CV enregistré');
      }
      setStep('done');
      fetchCVList();
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  const handleDownload = async (cv) => {
    try {
      // If translated version is active and we have its URL, use it
      if (isTranslated && translatedUrl) { window.open(translatedUrl, '_blank'); return; }
      if (cv.url) { window.open(cv.url, '_blank'); return; }
      const res = await cvAPI.regenerateDocx(cv.id || cvId);
      if (res.data?.url) window.open(res.data.url, '_blank');
      else toast.error('URL non disponible');
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleTranslate = async () => {
    if (!cvId) { toast.error('Enregistrez d\'abord le CV pour pouvoir le traduire'); return; }
    setTranslating(true);
    try {
      const res = await cvAPI.translate(cvId);
      const translatedJson = res.data.translated_json;
      // Save French version for toggle-back, then swap the form to English
      setFrenchCvData(cvData);
      setCvData(translatedJson);
      setTranslatedUrl(res.data.translated_url || null);
      setIsTranslated(true);
      toast.success('CV traduit en anglais — le formulaire affiche maintenant la version EN');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setTranslating(false);
    }
  };

  const handleToggleTranslation = () => {
    if (isTranslated) {
      // Restore French data
      if (frenchCvData) setCvData(frenchCvData);
      setIsTranslated(false);
      toast.success('Version française active');
    } else {
      handleTranslate();
    }
  };

  const handleSendToCandidate = async () => {
    try {
      await cvAPI.sendToCandidate(cvId, {
        candidate_email: sendEmail,
        candidate_name: cvData?.personal_info?.trigramme || '',
      });
      toast.success('Email envoyé au candidat');
      setSendModal(false);
      setSendEmail('');
    } catch (e) { toast.error(extractError(e)); }
  };

  const setPersonal = (k, v) =>
    setCvData(d => ({ ...d, personal_info: { ...d.personal_info, [k]: v } }));

  const addExp = () => setCvData(d => ({
    ...d,
    experiences: [...(d.experiences || []), {
      entreprise: '', dates: '', poste: '', synopsis: '',
      realisations: [''], methodologies: [], technologies: []
    }]
  }));

  const updateExp = (i, k, v) => setCvData(d => {
    const exp = [...d.experiences]; exp[i] = { ...exp[i], [k]: v };
    return { ...d, experiences: exp };
  });

  const removeExp = (i) => setCvData(d => ({ ...d, experiences: d.experiences.filter((_, idx) => idx !== i) }));

  const addTask = (ei) => setCvData(d => {
    const exp = [...d.experiences];
    exp[ei] = { ...exp[ei], realisations: [...(exp[ei].realisations || []), ''] };
    return { ...d, experiences: exp };
  });

  const updateTask = (ei, ti, v) => setCvData(d => {
    const exp = [...d.experiences];
    const r = [...(exp[ei].realisations || [])]; r[ti] = v;
    exp[ei] = { ...exp[ei], realisations: r };
    return { ...d, experiences: exp };
  });

  const removeTask = (ei, ti) => setCvData(d => {
    const exp = [...d.experiences];
    exp[ei].realisations = exp[ei].realisations.filter((_, i) => i !== ti);
    return { ...d, experiences: exp };
  });

  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));
  const stepIdx = STEPS.indexOf(step);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Conversion CV</h1>
          <p>
            {selectedCv
              ? `Édition : ${selectedCv.nom_proprietaire || 'CV sans nom'}`
              : 'Convertissez vos CV au format Wilkeys'}
          </p>
        </div>
        {selectedCv && (
          <div className="page-header-actions">
            <button className="btn btn-ghost btn-sm" onClick={closeCvEdit}>
              <X size={15}/> Fermer l'édition
            </button>
          </div>
        )}
      </div>

      {/* Progress — masqué en mode réédition depuis la liste */}
      {!selectedCv && (
        <div className="cv-progress">
          {[
            { key: 'upload',  label: 'Upload' },
            { key: 'extract', label: 'Extraction IA' },
            { key: 'edit',    label: 'Édition' },
            { key: 'done',    label: 'Terminé' },
          ].map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`cv-step ${stepIdx >= i ? 'active' : ''} ${stepIdx > i ? 'done' : ''}`}>
                <div className="cv-step-dot">{stepIdx > i ? <Check size={12}/> : i + 1}</div>
                <span>{s.label}</span>
              </div>
              {i < 3 && <div className={`cv-step-line ${stepIdx > i ? 'done' : ''}`}/>}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Bandeau info réédition */}
      {selectedCv && (
        <div style={{ padding: '12px 16px', background: 'var(--blue-50)', border: '1px solid var(--blue-200)', borderRadius: 'var(--radius-md)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem', color: 'var(--blue-700)' }}>
          <Eye size={16}/>
          <span>Vous éditez le CV de <strong>{selectedCv.nom_proprietaire || 'ce candidat'}</strong> — converti le {fmtDate(selectedCv.date_conversion || selectedCv.created_at)}</span>
        </div>
      )}

      <div className="cv-layout">
        {/* Main */}
        <div className="cv-main">

          {/* UPLOAD */}
          {step === 'upload' && (
            <div className="card">
              <div className="card-header"><span className="card-title">Uploader un CV</span></div>
              <div {...getRootProps()} className={`upload-zone ${isDragActive ? 'drag-over' : ''}`}>
                <input {...getInputProps()} />
                <Upload size={32} color={isDragActive ? 'var(--blue-600)' : 'var(--gray-400)'}/>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
                  {isDragActive ? 'Déposez ici…' : 'Glissez votre CV ou cliquez pour parcourir'}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>PDF, DOC, DOCX, TXT</div>
                <button type="button" className="btn btn-secondary btn-sm">Parcourir</button>
              </div>
              {file && (
                <div className="cv-file-selected">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileText size={18} color="var(--blue-600)"/>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{file.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{(file.size / 1024).toFixed(0)} Ko</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}><X size={14}/> Retirer</button>
                    <button className="btn btn-orange" onClick={handleConvert} disabled={loading}>
                      <RefreshCw size={15}/> Lancer l'extraction IA
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EXTRACT */}
          {step === 'extract' && (
            <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div className="cv-extract-anim">
                <div className="cv-extract-ring"/>
                <FileText size={28} color="var(--blue-600)"/>
              </div>
              <h3 style={{ marginTop: 24, marginBottom: 8 }}>Extraction en cours…</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                L'IA analyse le CV et structure les informations.
              </p>
            </div>
          )}

          {/* EDIT */}
          {(step === 'edit' || step === 'done') && cvData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Personal Info */}
              <div className="card">
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => toggle('personal')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <User size={16} color="var(--blue-600)"/>
                    <span className="card-title">Informations personnelles</span>
                  </div>
                  {expanded.personal ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                </div>
                {expanded.personal && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {[
                      { k: 'prenom',            label: 'Prénom' },
                      { k: 'nom',               label: 'Nom' },
                      { k: 'trigramme',         label: 'Trigramme' },
                      { k: 'poste',             label: 'Titre / Poste actuel' },
                      { k: 'annees_experience', label: "Années d'expérience" },
                    ].map(({ k, label }) => (
                      <div key={k} className="input-group">
                        <label className="input-label">{label}</label>
                        <input className="input" value={cvData.personal_info?.[k] || ''} onChange={e => setPersonal(k, e.target.value)}/>
                      </div>
                    ))}
                    <div className="input-group" style={{ gridColumn: '1/-1' }}>
                      <label className="input-label">Présentation professionnelle</label>
                      <textarea className="textarea" rows={3} value={cvData.personal_info?.presentation || ''} onChange={e => setPersonal('presentation', e.target.value)}/>
                    </div>
                  </div>
                )}
              </div>

              {/* Expériences */}
              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Briefcase size={16} color="var(--blue-600)"/>
                    <span className="card-title">Expériences professionnelles</span>
                    <span className="badge badge-blue">{cvData.experiences?.length || 0}</span>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={addExp}><Plus size={13}/> Ajouter</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {(cvData.experiences || []).map((exp, i) => (
                    <div key={i} className="cv-exp-block">
                      <div className="cv-exp-header">
                        <GripVertical size={14} color="var(--text-muted)" style={{ cursor: 'grab' }}/>
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div className="input-group">
                            <label className="input-label">Poste</label>
                            <input className="input" value={exp.poste || ''} onChange={e => updateExp(i, 'poste', e.target.value)}/>
                          </div>
                          <div className="input-group">
                            <label className="input-label">Entreprise</label>
                            <input className="input" value={exp.entreprise || ''} onChange={e => updateExp(i, 'entreprise', e.target.value)}/>
                          </div>
                          <div className="input-group" style={{ gridColumn: '1/-1' }}>
                            <label className="input-label">Dates</label>
                            <input className="input" value={exp.dates || ''} onChange={e => updateExp(i, 'dates', e.target.value)} placeholder="Ex: Janvier 2022 - Présent"/>
                          </div>
                          <div className="input-group" style={{ gridColumn: '1/-1' }}>
                            <label className="input-label">Synopsis / Contexte</label>
                            <input className="input" value={exp.synopsis || ''} onChange={e => updateExp(i, 'synopsis', e.target.value)}/>
                          </div>
                        </div>
                        <button className="btn btn-danger btn-icon btn-sm" onClick={() => removeExp(i)}><Trash2 size={13}/></button>
                      </div>
                      <div className="cv-tasks">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Réalisations</div>
                          <button className="btn btn-ghost btn-sm" onClick={() => addTask(i)}><Plus size={12}/> Ajouter</button>
                        </div>
                        {(exp.realisations || []).map((t, ti) => (
                          <div key={ti} className="cv-task-row">
                            <div className="cv-task-dot"/>
                            <input className="input cv-task-input" value={t} onChange={e => updateTask(i, ti, e.target.value)} placeholder="Description de la réalisation…"/>
                            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => removeTask(i, ti)}><X size={12}/></button>
                          </div>
                        ))}
                      </div>
                      <div className="input-group" style={{ marginTop: 8 }}>
                        <label className="input-label" style={{ fontSize: '0.6875rem' }}>Technologies (séparées par virgule)</label>
                        <input className="input" style={{ height: 32, fontSize: '0.8125rem' }}
                          value={(exp.technologies || []).join(', ')}
                          onChange={e => updateExp(i, 'technologies', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Langues */}
              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Globe size={16} color="var(--blue-600)"/>
                    <span className="card-title">Langues</span>
                  </div>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setCvData(d => ({ ...d, langues: [...(d.langues || []), { langue: '', niveau: '' }] }))}>
                    <Plus size={13}/> Ajouter
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {(cvData.langues || []).map((l, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                      <div className="input-group" style={{ flex: 1 }}>
                        <label className="input-label">Langue</label>
                        <input className="input" value={l.langue || ''} onChange={e => {
                          const lng = [...cvData.langues]; lng[i] = { ...lng[i], langue: e.target.value };
                          setCvData(d => ({ ...d, langues: lng }));
                        }}/>
                      </div>
                      <div className="input-group" style={{ flex: 1 }}>
                        <label className="input-label">Niveau</label>
                        <select className="input select" value={l.niveau || ''} onChange={e => {
                          const lng = [...cvData.langues]; lng[i] = { ...lng[i], niveau: e.target.value };
                          setCvData(d => ({ ...d, langues: lng }));
                        }}>
                          {['','Débutant','Intermédiaire','Avancé','Courant','Bilingue','Natif'].map(n => <option key={n}>{n}</option>)}
                        </select>
                      </div>
                      <button className="btn btn-danger btn-icon btn-sm" style={{ marginBottom: 1 }}
                        onClick={() => setCvData(d => ({ ...d, langues: d.langues.filter((_, idx) => idx !== i) }))}>
                        <X size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Award size={16} color="var(--blue-600)"/>
                    <span className="card-title">Certifications</span>
                  </div>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setCvData(d => ({ ...d, certifications: [...(d.certifications || []), { titre: '', etablissement: '', annee: '' }] }))}>
                    <Plus size={13}/> Ajouter
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(cvData.certifications || []).map((c, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
                      <div className="input-group">
                        <label className="input-label">Titre</label>
                        <input className="input" value={c.titre || ''} onChange={e => {
                          const certs = [...cvData.certifications]; certs[i] = { ...certs[i], titre: e.target.value };
                          setCvData(d => ({ ...d, certifications: certs }));
                        }}/>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Organisme</label>
                        <input className="input" value={c.etablissement || ''} onChange={e => {
                          const certs = [...cvData.certifications]; certs[i] = { ...certs[i], etablissement: e.target.value };
                          setCvData(d => ({ ...d, certifications: certs }));
                        }}/>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Année</label>
                        <input className="input" value={c.annee || ''} onChange={e => {
                          const certs = [...cvData.certifications]; certs[i] = { ...certs[i], annee: e.target.value };
                          setCvData(d => ({ ...d, certifications: certs }));
                        }} placeholder="YYYY"/>
                      </div>
                      <button className="btn btn-danger btn-icon btn-sm" style={{ marginBottom: 1 }}
                        onClick={() => setCvData(d => ({ ...d, certifications: d.certifications.filter((_, idx) => idx !== i) }))}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Formations */}
              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GraduationCap size={16} color="var(--blue-600)"/>
                    <span className="card-title">Formations</span>
                  </div>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setCvData(d => ({ ...d, formations: [...(d.formations || []), { diplome: '', ecole: '', annee: '' }] }))}>
                    <Plus size={13}/> Ajouter
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(cvData.formations || []).map((f, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
                      <div className="input-group">
                        <label className="input-label">Diplôme</label>
                        <input className="input" value={f.diplome || ''} onChange={e => {
                          const fms = [...cvData.formations]; fms[i] = { ...fms[i], diplome: e.target.value };
                          setCvData(d => ({ ...d, formations: fms }));
                        }}/>
                      </div>
                      <div className="input-group">
                        <label className="input-label">École</label>
                        <input className="input" value={f.ecole || ''} onChange={e => {
                          const fms = [...cvData.formations]; fms[i] = { ...fms[i], ecole: e.target.value };
                          setCvData(d => ({ ...d, formations: fms }));
                        }}/>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Année</label>
                        <input className="input" value={f.annee || ''} onChange={e => {
                          const fms = [...cvData.formations]; fms[i] = { ...fms[i], annee: e.target.value };
                          setCvData(d => ({ ...d, formations: fms }));
                        }}/>
                      </div>
                      <button className="btn btn-danger btn-icon btn-sm" style={{ marginBottom: 1 }}
                        onClick={() => setCvData(d => ({ ...d, formations: d.formations.filter((_, idx) => idx !== i) }))}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action bar sticky */}
              <div className="card cv-action-bar">
                <button className="btn btn-secondary" onClick={() => { setStep('upload'); setFile(null); setCvData(null); setCvId(null); setSelectedCv(null); _resetTranslation(); }}>
                  <RefreshCw size={15}/> {selectedCv ? 'Fermer' : 'Recommencer'}
                </button>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {cvId && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDownload({ id: cvId })}
                        title={isTranslated ? 'Télécharger la version anglaise' : 'Télécharger DOCX'}
                      >
                        <Download size={15}/>
                        {isTranslated ? 'Télécharger (EN)' : 'Télécharger'}
                      </button>

                      <button
                        className={`btn ${isTranslated ? 'btn-orange' : 'btn-secondary'}`}
                        onClick={handleToggleTranslation}
                        disabled={translating}
                        title={isTranslated ? 'Revenir à la version française' : 'Traduire en anglais via IA'}
                      >
                        {translating
                          ? <div className="spinner" style={{ width: 14, height: 14 }}/>
                          : <Languages size={15}/>}
                        {translating ? 'Traduction…' : isTranslated ? 'Version FR' : 'Traduire EN'}
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => { setSendEmail(emailSurCv || cvData?.personal_info?.email || ''); setSendModal(true); }}
                      >
                        <Send size={15}/> Envoyer au candidat
                      </button>
                    </>
                  )}
                  <button className="btn btn-orange" onClick={handleSave} disabled={loading}>
                    {loading ? <div className="spinner" style={{ width: 15, height: 15 }}/> : <Save size={15}/>}
                    {loading ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar CVs */}
        <div className="cv-sidebar">
          <div className="card" style={{ position: 'sticky', top: 20 }}>
            <div className="card-header">
              <span className="card-title">Mes CV convertis</span>
              <span className="badge badge-blue">{cvList.length}</span>
            </div>
            <div className="cv-list">
              {cvList.length === 0 && (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <FileText size={24}/>
                  <p style={{ fontSize: '0.8125rem' }}>Aucun CV converti</p>
                </div>
              )}
              {cvList.map(cv => (
                <div
                  key={cv.id}
                  className="cv-list-item"
                  style={{
                    background: selectedCv?.id === cv.id ? 'var(--orange-50)' : undefined,
                    border: selectedCv?.id === cv.id ? '1px solid var(--blue-200)' : '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <div className="avatar avatar-sm">{initials(cv.nom_proprietaire || 'CV')}</div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cv.nom_proprietaire || cv.original_filename || 'Sans nom'}
                    </div>
                    <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                      {fmtDate(cv.date_conversion || cv.created_at)}
                    </div>
                  </div>
                  {/* Bouton Visualiser / Éditer */}
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    title="Visualiser et éditer"
                    onClick={() => openCvForEdit(cv)}
                    style={{ color: selectedCv?.id === cv.id ? 'var(--blue-600)' : undefined }}
                  >
                    <Eye size={14}/>
                  </button>
                  {/* Bouton Télécharger */}
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    title="Télécharger DOCX"
                    onClick={() => handleDownload(cv)}
                  >
                    <Download size={13}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Send Modal */}
      {sendModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSendModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>Envoyer au candidat</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setSendModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Email du candidat <span>*</span></label>
                <input
                  className="input"
                  type="email"
                  placeholder="candidat@email.com"
                  value={sendEmail}
                  onChange={e => setSendEmail(e.target.value)}
                />
                {sendEmail && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--green-600)', marginTop: 4, display: 'block' }}>
                    Email pré-rempli depuis le CV
                  </span>
                )}
              </div>
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--blue-50)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem', color: 'var(--blue-700)' }}>
                Le candidat recevra un lien sécurisé pour réviser son CV avant de vous le renvoyer.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSendModal(false)}>Annuler</button>
              <button className="btn btn-orange" onClick={handleSendToCandidate} disabled={!sendEmail}>
                <Send size={15}/> Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}